/* global Promise */
/* eslint-disable no-underscore-dangle */

import sessionReader from '../../store/readHelpers/SessionReader';
import apiActions from '../../actions/ApiActions';
import apiTools from './ApiTools';
import { API_ROOT, ASSESSMENT_ROOT, DASHBOARD_ROOT, methods, safeBrowserLinks } from './ApiConstants';
import storeManager from '../../store/StoreManager';
import connectionStatusConstants from '../../constants/ConnectionStatusConstants';
import settingsReader from '../../store/readHelpers/SettingsReader';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import stateCacheService from '../StateCache';
import questionReader from '../../store/readHelpers/QuestionReader';
import reloginService from '../ReloginService';
import utilService from '../UtilService';
import SessionConstants from '../../constants/SessionConstants';

const throttle = require('lodash.throttle');

/**
 * @param eventType
 * @param extraInfo
 * @param arId
 * @returns {Promise.<T>} See: handleApiResponse
 */
function _notifyClientEvent(eventType, extraInfo, arId) {
    const assessmentRunId = arId || sessionReader.getAssessmentRunId();

    // If no ARID is given we can't log the event
    if (!assessmentRunId || !sessionReader.isContextExam()) {
        return Promise.resolve('No ARID or not in exam context');
    }

    const url = apiTools.prependDomain(`${API_ROOT}${methods.NOTIFY_CLIENT_EVENT}`);
    const params = {
        assessmentRunId,
        eventType,
        extraInfo,
    };

    console.log(`Sending NotifyClientEvent, params: ${JSON.stringify(params)}`);
    const request = apiTools.makeJsonPostRequest(url, params);

    return apiTools
        .executeRequest(request)
        .catch(() => {
            // No need to do anything. This is just to prevent unhandled rejection errors.
        });
}

const apiService = {
    /**
     * Stores the user response(s) for a question.
     *
     * @param itemResultXML
     * @param questionId
     * @param additionalData Map of extra data to send
     * @returns {Promise.<T>} See: handleApiResponse
     */
    storeUserResponse(itemResultXML, questionId, additionalData = {}) {
        const state = storeManager.getState();
        const isPreview = sessionReader.isContextPreview();
        const assessmentRunId = sessionReader.getAssessmentRunId();
        const { contentItemId } = state.assessment;
        let url = apiTools.prependDomain(`${API_ROOT}${methods.STORE_USER_RESPONSE}`);
        if (isPreview && sessionReader.isDataSetTypeQuestionSet()) {
            url = `${url}?contentItemId=${contentItemId}`;
        }

        const data = {
            itemResultXML,
            questionId,
            isPreview,
        };

        if (
            assessmentRunId && (
                !sessionReader.getPrintParam(sessionReader.printParamNames.SHOW_PREVIEW_WITH_FEEDBACK, false) ||
                (sessionReader.isParentTest() && sessionReader.isContextReadinessCheck())
            )
        ) {
            data.assessmentRunId = assessmentRunId;
        }

        if (contentItemId) {
            data.contentItemId = contentItemId;
        }

        if (settingsReader.isCatTestType() && state.cat.currentCatRevisionId !== '') {
            data.catRevisionId = state.cat.currentCatRevisionId;
        }

        if (settingsReader.isCatTestType() && sessionReader.isSectionItemBankLevel()) {
            const question = assessmentReader.getQuestion(questionId);
            data.parentSectionRevisionId = question.parentId;
            data.sectionNumber = question.sectionNumber;
        }

        if (reloginService.shouldStoreNextQuestion()) {
            data.nextQuestionId = questionReader.getNextQuestionContentRevisionId(questionId);
        }

        const request = apiTools.makeJsonPostRequest(url, Object.assign({}, additionalData, data));
        return apiTools.executeRequest(request);
    },

    /**
     * Fetches the stored user responses from the server.
     *
     * @returns {Promise.<T>} See: handleApiResponse
     */
    fetchUserResponses() {
        const partyId = sessionReader.getPartyId();
        let url = apiTools.prependAndAddIdentifier(methods.GET_USER_RESPONSES);

        if (partyId) {
            url = `${url}&partyId=${partyId}`;
        }

        if (sessionReader.isContextPreview()) {
            url = `${url}&isPreview=true`;
        }

        if (sessionReader.getRequestingPartyId() > 0) {
            url = `${url}&requestingPartyId=${sessionReader.getRequestingPartyId()}`;
        }

        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    fetchUserResponseForOneQuestion(contentRevisionId) {
        const partyId = sessionReader.getPartyId();
        let url = `${API_ROOT}${methods.GET_USER_RESPONSE}?questionId=${contentRevisionId}`;

        if (sessionReader.isContextPreview() && sessionReader.isDataSetTypeQuestionSet()) {
            const questionSetContentItemId = sessionReader.getContentItemId();
            url = `${url}&questionSetContentItemId=${questionSetContentItemId}`;
        }

        if (partyId && partyId > -1) {
            url = `${url}&partyId=${partyId}`;
        }

        const assessmentRunId = sessionReader.getAssessmentRunId();
        if (assessmentRunId && !sessionReader.getPrintParam(sessionReader.printParamNames.SHOW_PREVIEW_WITH_FEEDBACK, false)) {
            url = `${url}&assessmentRunId=${assessmentRunId}`;
        }

        if (assessmentRunId && sessionReader.isContextReadinessCheck()) {
            url = `${url}&isReadinessCheckContext=true`;
        }

        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    /**
     * Fetches a single question from the server.
     *
     * @param {string|null|undefined} payload - Optional question or stimulus revision ID
     *     to override the default identifier when building the API URL.
     *     If null or undefined, the default question identifier is used.
     * @returns {Promise.<T>} See: handleApiResponse
     */
    fetchQuestion(payload) {
        const partyId = sessionReader.getPartyId();
        const safePayload = payload || null;
        let url = apiTools.prependAndAddIdentifier(methods.GET_QUESTION, safePayload);
        url = apiTools.appendViewAsForGetContentApis(url);

        if (sessionReader.getAssessmentRunId()) {
            url = `${url}&contextAssessmentRunId=${sessionReader.getAssessmentRunId()}`;
        }

        if (partyId) {
            url = `${url}&partyId=${partyId}`;
        }

        if (utilService.isQueryParameterTrue('isPreviewMode')) {
            url += '&isPreviewMode=true';
        }

        if (utilService.isQueryParameterTrue('isReviewMode')) {
            url += '&isReviewMode=true';
        }

        if (sessionReader.getContext() === SessionConstants.CONTEXT_PREVIEW_AUDIO_SCRIPT) {
            url += '&isPreviewAudioScript=true';
        }

        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    /**
     * Fetches a single QTI section from the server.
     *
     * @returns {Promise.<T>} See: handleApiResponse
     */
    fetchSection() {
        let url = apiTools.prependAndAddIdentifier(methods.GET_SECTION);
        url = apiTools.appendViewAsForGetContentApis(url);

        if (sessionReader.getAssessmentRunId()) {
            url += `&contextAssessmentRunId=${sessionReader.getAssessmentRunId()}`;
        }

        if (utilService.isQueryParameterTrue('isPreviewMode')) {
            url += '&isPreviewMode=true';
        }

        if (utilService.isQueryParameterTrue('isReviewMode')) {
            url += '&isReviewMode=true';
        }

        if (apiTools.shouldAddAudioScriptContext()) {
            url += `&context=${SessionConstants.CONTEXT_PREVIEW_AUDIO_SCRIPT}`;
        }

        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    /**
     * Fetches the question set from the server.
     *
     * @returns {Promise.<T>} See: handleApiResponse
     */
    fetchQuestionSet() {
        const partyId = sessionReader.getPartyId();
        let url = apiTools.prependAndAddIdentifier(methods.GET_QUESTION_SET);
        url = apiTools.appendViewAsForGetContentApis(url);

        if (partyId) {
            url = `${url}&partyId=${partyId}`;
        }

        const context = storeManager.getState().session.context;
        if (context) {
            url = `${url}&context=${context}`;
        }

        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    /**
     * Fetches the CAT question set from the server.
     *
     * @returns {Promise.<T>} See: handleApiResponse
     */
    fetchCatQuestionSet(revisionId) {
        let url = apiTools.prependAndAddIdentifier(methods.GET_QUESTION_SET);
        url = apiTools.appendViewAsForGetContentApis(url);

        url = `${url}&requestCatAssessment=${true}&catRevisionId=${revisionId}`;

        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    fetchRemoteOralExamSessionInfo() {
        const url = apiTools.prependAndAddIdentifier(methods.GET_REMOTE_ORAL_EXAM_SESSION_INFO);
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    /**
     * Fetches the question set from the server.
     *
     * @returns {Promise.<T>} See: handleApiResponse
     */
    fetchChildQuestionSet(parentAssessmentRunId, assessmentRunId, isReadinessCheck) {
        const params = new URLSearchParams();
        if (isReadinessCheck) {
            params.set('assessmentRunId', parentAssessmentRunId);
            params.set('contentRevisionId', assessmentRunId);
        } else {
            params.set('assessmentRunId', assessmentRunId);
        }
        const url = apiTools.prependDomain(`${API_ROOT}${methods.GET_QUESTION_SET}?${params.toString()}`);

        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    /**
     * Fetches the parent test from the server.
     *
     * @returns {Promise.<T>} See: handleApiResponse
     */
    fetchChildTests() {
        const url = apiTools.prependAndAddIdentifier(methods.GET_CHILD_TESTS);
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    /**
     * Fetches the settings from the server.
     *
     * @returns {Promise.<T>} See: handleApiResponse
     */
    fetchSettings() {
        let url = apiTools.prependAndAddIdentifier(methods.GET_SETTINGS);
        if ((sessionReader.isDataSetTypeQuestion() || sessionReader.isDataSetTypeSection())
            && (sessionReader.isContextAnyViewDeliveryType() || sessionReader.isContextViewGradingQuestionPreview())) {
            const qsId = {
                paramName: 'assessmentRunId',
                paramValue: sessionReader.getAssessmentRunId(),
            };
            url = apiTools.prependDomain(`${API_ROOT}${methods.GET_SETTINGS}?${qsId.paramName}=${qsId.paramValue}`);
        }
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    fetchChildSettings(parentAssessmentRunId, assessmentRunId, isReadinessCheck) {
        const params = new URLSearchParams();
        if (isReadinessCheck) {
            params.set('assessmentRunId', parentAssessmentRunId);
            params.set('contentRevisionId', assessmentRunId);
        } else {
            params.set('assessmentRunId', assessmentRunId);
        }
        const url = apiTools.prependDomain(`${API_ROOT}${methods.GET_SETTINGS}?${params.toString()}`);
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    fetchCorrectResponsesForReadinessCheck(parentAssessmentRunId, readinessCheckContentRevisionId) {
        const params = new URLSearchParams();
        params.set('assessmentRunId', parentAssessmentRunId);
        params.set('contentRevisionId', readinessCheckContentRevisionId);
        const url = apiTools.prependDomain(`${API_ROOT}${methods.GET_CORRECT_RESPONSES}?${params.toString()}`);
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    fetchCorrectResponses(data = {}) {
        const isRichCorrectResponses = assessmentReader.isRichCorrectResponses();
        const partyId = sessionReader.getPartyId();
        let url = apiTools.prependAndAddIdentifier(methods.GET_CORRECT_RESPONSES);
        const { paramName } = sessionReader.getQuestionSetIdentifier();

        if (paramName !== 'assessmentRunId') {
            const assessmentRunId = sessionReader.getAssessmentRunId();
            if (assessmentRunId) {
                url = `${url}&assessmentRunId=${assessmentRunId}`;
            }
        }

        if (partyId) {
            url = `${url}&partyId=${partyId}`;
        }

        if (Object.keys(data).length > 0) {
            const params = new URLSearchParams(data);
            url = `${url}&${params}`;
        }

        if (isRichCorrectResponses) {
            url = `${url}&richCorrectResponses=true`;
        }

        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    fetchDeliveryDetails() {
        const url = `${apiTools.prependAndAddIdentifier(methods.GET_DELIVERY_DETAILS)}&partyId=${sessionReader.getPartyId()}`;
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    fetchSpreadSheetIframeUrl(questionId) {
        const assessmentRunId = sessionReader.getAssessmentRunId();
        const userId = sessionReader.getUserId();
        const url = this.createSpreadSheetIframeURL(questionId, assessmentRunId, userId);
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    createSpreadSheetIframeURL(questionId, assessmentRunId, userId) {
        if (!questionId) {
            return `${ASSESSMENT_ROOT}${methods.GET_TEST_TAKER_O365_FILE_METADATA}?assessmentRunId=${assessmentRunId}&userId=${userId}`;
        }
        return `${ASSESSMENT_ROOT}${methods.GET_O365_FILE_URL}?questionId=${questionId}`;
    },

    fetchSketchCodesForQuestions() {
        let url = `${API_ROOT}${methods.GET_SKETCH_CODES}`;

        const assessmentRunId = sessionReader.getAssessmentRunId();
        let partyId;
        if (sessionReader.isContextExam()) {
            partyId = sessionReader.getUserId();
        } else {
            partyId = sessionReader.getPartyId();
        }

        if (assessmentRunId) {
            url = `${url}?assessmentRunId=${assessmentRunId}`;
        }

        if (partyId) {
            url = `${url}&partyId=${partyId}`;
        }

        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(apiTools.prependDomain(url))
        );
    },

    fetchSketchCodeForQuestion(questionId) {
        let url = `${API_ROOT}${methods.GET_SKETCH_CODE_FOR_QUESTION}`;

        url += `?questionId=${questionId}`;
        url += `&assessmentRunId=${sessionReader.getAssessmentRunId()}`;

        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(apiTools.prependDomain(url))
        );
    },

    fetchFeedbackForQuestions() {
        let url = `${API_ROOT}${methods.GET_FEEDBACK}`;

        const assessmentRunId = sessionReader.getAssessmentRunId();
        let partyId;
        if (sessionReader.isContextExam()) {
            partyId = sessionReader.getUserId();
        } else {
            partyId = sessionReader.getPartyId();
        }

        if (assessmentRunId) {
            url = `${url}?assessmentRunId=${assessmentRunId}`;
        }

        if (partyId) {
            url = `${url}&partyId=${partyId}`;
        }

        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(apiTools.prependDomain(url))
        );
    },

    fetchFeedbackForQuestion(questionId) {
        let url = `${API_ROOT}${methods.GET_FEEDBACK}`;

        const assessmentRunId = sessionReader.getAssessmentRunId();
        let partyId;
        if (sessionReader.isContextExam()) {
            partyId = sessionReader.getUserId();
        } else {
            partyId = sessionReader.getPartyId();
        }

        url = `${url}?contentRevisionId=${questionId}`;

        if (assessmentRunId && sessionReader.getPrintParam(sessionReader.printParamNames.SHOW_PREVIEW_WITH_FEEDBACK, false)) {
            url = `${url}&assessmentRunId=${assessmentRunId}&isFeedbackPreview=true`;
        }

        if (partyId) {
            url = `${url}&partyId=${partyId}`;
        }

        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(apiTools.prependDomain(url))
        );
    },

    fetchPrechecksSettings() {
        const assessmentRunId = sessionReader.getAssessmentRunId();
        const url = apiTools.prependDomain(
            `${API_ROOT}${methods.GET_PRECHECKS_SETTINGS}?assessmentRunId=${assessmentRunId}`,
        );
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    startTest(useParent = true, useRetry = true) {
        const assessmentRunId = sessionReader.getAssessmentRunId(useParent);
        const url = apiTools.prependDomain(`${API_ROOT}StartTest?assessmentRunId=${assessmentRunId}`);
        const data = {};

        if (sessionReader.shouldStopTimeUponExit() && !useParent) {
            const latestTimestamp = stateCacheService.getStateCacheInstance().getLastExposedToTestTimestampObject(assessmentRunId);
            if (latestTimestamp?.lastExposedToTestTimestamp) {
                data.lastExposedToTestTimestamp = latestTimestamp.lastExposedToTestTimestamp;
            }
        }

        try {
            const pwaAppToken = stateCacheService.getStateCacheInstance().getPwaAppToken();
            if (pwaAppToken) {
                data.pwaAppToken = pwaAppToken;
            }
        } catch (e) {
            console.error('Failed to get pwa app token', e);
        }

        const request = apiTools.makeFormDataPostRequest(url, data);
        const store = storeManager.getStore();

        if (sessionReader.isOfflineMode()) {
            store.dispatch(apiActions.setApiConnectionStatus(connectionStatusConstants.OFFLINE_MODE));
            store.dispatch(apiActions.setIsOnline(false));
            return Promise.resolve();
        }

        if (useRetry) {
            return apiTools.executeRequestWithRetry(request);
        }
        return apiTools.executeRequest(request);
    },

    fetchUnreadMessages() {
        const url = apiTools.prependDomain(
            `${ASSESSMENT_ROOT}${methods.GET_UNREAD_MESSAGES}`,
        );
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    fetchMessages() {
        const page = 1;
        const hitsPerPage = 10; // Matches number in dashboard
        const url = apiTools.prependDomain(
            `${ASSESSMENT_ROOT}${methods.GET_MESSAGES}?page=${page}&hitsPerPage=${hitsPerPage}`,
        );
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    fetchRemainingTime(assessmentRunId, withRetry, skipFrontCache = false) {
        let url = apiTools.prependDomain(
            `${API_ROOT}${methods.GET_REMAINING_TIME}?assessmentRunId=${assessmentRunId}`,
        );
        if (skipFrontCache) {
            url = `${url}&skipFrontCacheForUserTestSetup=true`;
        }
        const request = apiTools.makeGetRequest(url);
        if (withRetry) {
            return apiTools.executeRequestWithRetry(request);
        }
        return apiTools.executeRequest(request);
    },

    fetchLoggedInUser() {
        const url = apiTools.prependDomain(`${ASSESSMENT_ROOT}${methods.GET_DESCRIBE_LOGGED_IN_USER}`);
        const request = apiTools.makeGetRequest(url);
        return apiTools.executeRequest(request)
            .then((response) => {
                return response.user;
            })
            .catch((error) => {
                window.console.error('Error fetching user data for accessToken:', error);
            });
    },

    async fetchSpreadSheetAccessToken() {
        const userDetails = await this.fetchLoggedInUser();
        const queryParams = {
            userId: userDetails.userId,
        };
        const url = apiTools.prependDomain(`${ASSESSMENT_ROOT}${methods.GET_SS_ACCESS_TOKEN}?${new URLSearchParams(queryParams)}`);
        const request = apiTools.makeJsonPostRequest(url, { exampleKey: 'exampleValue' });
        return apiTools.executeRequest(request);
    },

    /**
     * Gets a new signed websocket url from server
     * @param useRetry If retry mechanism in ApiService should be used or not
     * @returns {Promise.<T>}
     */
    fetchSignedWebSocketUrl(useRetry) {
        const url = apiTools.prependAndAddIdentifier(methods.GET_SIGNED_WEB_SOCKET_URL);
        const request = apiTools.makeGetRequest(url);

        if (useRetry) {
            return apiTools.executeRequestWithRetry(request);
        }
        return apiTools.executeRequest(request);
    },

    /**
     * Calls NotifyClientEvent for the current assessmentRunId
     * The method is throttled by default to avoid spamming the server if a repeating error happens
     * @param eventType
     * @param extraInfo
     * @param noThrottle - use noThrottle = true when chaining several calls of this method.
     * @param assessmentRunId
     * @returns {*}
     */
    notifyClientEvent(eventType, extraInfo, noThrottle = false, assessmentRunId) {
        if (noThrottle) {
            return this._notifyClientEventNoThrottle(eventType, extraInfo, assessmentRunId);
        }

        // Calling the pre-defined throttled method
        return this._notifyClientEventThrottle(eventType, extraInfo, assessmentRunId);
    },

    _notifyClientEventNoThrottle: _notifyClientEvent,

    _notifyClientEventThrottle: throttle(_notifyClientEvent, 1000),

    /**
     * Gets the status of the students answers stored on the server
     * @returns {Promise.<T>|*|Promise}
     */
    checkStoredItems() {
        const assessmentRunId = sessionReader.getAssessmentRunId();
        const url = apiTools.prependDomain(`${API_ROOT}${methods.CHECK_STORED_ITEMS}?assessmentRunId=${assessmentRunId}`);
        const request = apiTools.makeFormDataPostRequest(url);

        return apiTools.executeRequestWithRetry(request);
    },

    deliverTest(deliverMethod) {
        const assessmentRunId = sessionReader.getAssessmentRunId();
        const parameters = `?assessmentRunId=${assessmentRunId}`;
        const path = `${API_ROOT}${deliverMethod}${parameters}`;
        const url = apiTools.prependDomain(path);
        const request = apiTools.makeFormDataPostRequest(url);

        return apiTools.executeRequestWithRetry(request);
    },

    evaluateBranchRules(sectionId) {
        const assessmentRunId = sessionReader.getAssessmentRunId();
        const url = apiTools.prependDomain(`${API_ROOT}${methods.EVALUATE_BRANCH_RULE}?assessmentRunId=${assessmentRunId}&sectionId=${sectionId}`);
        const request = apiTools.makeGetRequest(url);

        return apiTools.executeRequestWithRetry(request);
    },

    /**
     * Gets info for use with the file uploader, eg:
     * {
     *  "accessKey": "AKIAIGY24SZA5ZBCS4GQ",
     *  "bucket":"insperalearning-dev",
     *  "awsUrl": "https://s3-eu-west-1.amazonaws.com",
     *  "maxFileSize": 1073741824
     *  }
     *
     * @returns {*|Promise|Promise.<T>}
     */
    getUploaderInfo() {
        const url = apiTools.prependDomain(`${ASSESSMENT_ROOT}${methods.GET_UPLOADER_INFO}`);
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    /**
     * @param {string} mimeType
     * @param {string} fileName
     * @param {string} fileSize
     * @param {string} questionId
     * @param assessmentRunContentId
     * @param _token
     * @returns {Promise.<T>|*|Promise} { mimeType: 'image/jpeg', ticket: 'dhfaiawpbXc', key: 'vj974hfq7f98347hf' }
     */
    getUploadTicket(mimeType, fileName, fileSize, questionId, assessmentRunContentId, _token) {
        const url = apiTools.prependDomain(`${ASSESSMENT_ROOT}${methods.GET_UPLOAD_TICKET}`);
        const request = apiTools.makeFormDataPostRequest(url, {
            _token,
            assessmentRunContentId,
            fileName,
            fileSize,
            mimeType,
            questionId,
        });

        return apiTools.executeRequestWithRetry(request);
    },

    sendUploadSuccess(ticket, questionId, assessmentRunContentId, _token, skipContentUrlCreation = false) {
        let url = apiTools.prependDomain(`${ASSESSMENT_ROOT}${methods.UPLOAD_SUCCESS}`);
        if (skipContentUrlCreation) {
            url += '?skipContentUrlCreation=true';
        }
        const request = apiTools.makeFormDataPostRequest(url, {
            _token,
            assessmentRunContentId,
            questionId,
            ticket,
        });

        return apiTools.executeRequestWithRetry(request);
    },

    getAssetUploadTicket(itemBankId, fileName, fileSize) {
        const url = apiTools.prependDomain(`${ASSESSMENT_ROOT}${methods.GET_ASSET_UPLOAD_TICKET}`);
        const request = apiTools.makeFormDataPostRequest(url, {
            itemBankId,
            fileName,
            fileSize,
        });
        return apiTools.executeRequestWithRetry(request);
    },

    assetUploadSuccess(itemBankId, ticket, title) {
        const url = apiTools.prependDomain(`${ASSESSMENT_ROOT}${methods.ASSET_UPLOAD_SUCCESS}`);
        const request = apiTools.makeFormDataPostRequest(url, {
            itemBankId,
            ticket,
            title,
        });
        return apiTools.executeRequestWithRetry(request);
    },

    getPresignedURLFromFileServletUrl(fileServletUrl) {
        const url = apiTools.prependDomain(fileServletUrl);
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    getTTSAccessInfo() {
        const url = apiTools.prependAndAddIdentifier(methods.GET_TTS_ACCESS_INFO);
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    getZoomTestCallDetail() {
        const url = apiTools.prependAndAddIdentifier(methods.GET_ZOOM_TEST_CALL_DETAIL);
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    getZoomHuichangTestCallDetail() {
        const url = apiTools.prependDomain(`${ASSESSMENT_ROOT}${methods.GET_ZOOM_HUICHANG_TEST_CALL_DETAIL}`);
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    checkSourceCodeCompilation(questionId, sourceCodeCompilationSubmissionId, checkAgainstEvaluationTestCases) {
        let url = checkAgainstEvaluationTestCases ?
            apiTools.prependAndAddIdentifier(methods.CHECK_CODE_EVAL_TEST_CASES)
            : apiTools.prependAndAddIdentifier(methods.CHECK_CODE_COMPILATION);

        url += `&questionId=${questionId}&sourceCodeCompilationSubmissionId=${sourceCodeCompilationSubmissionId}`;
        return apiTools.executeRequestWithRetry(
            apiTools.makeGetRequest(url)
        );
    },

    submitCodeForCompilation(questionId, userData, checkAgainstEvaluationTestCases) {
        const path = checkAgainstEvaluationTestCases ?
            apiTools.prependAndAddIdentifier(methods.SUBMIT_CODE_EVAL_TEST_CASES)
            : apiTools.prependAndAddIdentifier(methods.SUBMIT_CODE_FOR_COMPILATION);

        const request = apiTools.makeFormDataPostRequest(path, {
            sourceCode: userData,
            questionId,
        });

        return apiTools.executeRequest(request);
    },

    getTTSAudioUrl(ssml, voice) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.GET_TTS_AUDIO_URL}`);
        const qsId = sessionReader.getQuestionSetIdentifier();
        const { voiceId, engine } = voice;

        const request = apiTools.makeFormDataPostRequest(path, {
            [qsId.paramName]: qsId.paramValue,
            ssml,
            voiceId,
            engine,
        });

        return apiTools.executeRequest(request);
    },

    generateNTSToken(assessmentRunId) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.GENERATE_NTS_TOKEN}`);
        const request = apiTools.makeFormDataPostRequest(path, {
            assessmentRunId,
        });

        return apiTools.executeRequest(request);
    },


    startCountdown(assessmentRunId) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.START_COUNTDOWN}`);
        const request = apiTools.makeFormDataPostRequest(path, {
            assessmentRunId,
        });

        return apiTools.executeRequest(request);
    },

    enterOralExamWaitingRoom(assessmentRunId) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.ENTER_ORAL_EXAM_WAITING_ROOM}`);
        const request = apiTools.makeFormDataPostRequest(path, {
            assessmentRunId,
        });

        return apiTools.executeRequest(request);
    },

    sendRemoteOralExamStatus(assessmentRunId, contextDisplayStatus) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.SEND_REMOTE_ORAL_EXAM_STATUS}`);
        const request = apiTools.makeFormDataPostRequest(path, {
            assessmentRunId,
            contextDisplayStatus,
        });

        return apiTools.executeRequest(request);
    },

    uploadAutomaticIdVerificationPhotos(assessmentRunId, faceMatches) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.UPLOAD_AUTOMATIC_ID_VERIFICATION_PHOTOS}`);
        const params = { assessmentRunId };
        if (faceMatches) {
            params.faceMatchesJson = JSON.stringify(faceMatches);
        }
        const request = apiTools.makeFormDataPostRequest(path, params);

        return apiTools.executeRequest(request);
    },

    requestManualIdVerification(assessmentRunId) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.REQUEST_MANUAL_ID_VERIFICATION}`);
        const request = apiTools.makeFormDataPostRequest(path, {
            assessmentRunId,
        });

        return apiTools.executeRequest(request);
    },

    validateDayCode(assessmentRunId, password) {
        const path = `${DASHBOARD_ROOT}${methods.VALIDATE_DAY_CODE}`;
        const url = apiTools.prependDomain(path);

        const request = apiTools.makeFormDataPostRequest(url, {
            dayCode: password,
            assessmentRunId,
        });

        return apiTools.executeRequest(request);
    },

    setSectionAudioPosition(data) {
        const url = apiTools.prependAndAddIdentifier(methods.SET_BACKGROUND_AUDIO_POSITION);
        return apiTools.executeRequest(
            apiTools.makeJsonPostRequest(url, data)
        );
    },

    postFileDelivery(assessmentRunId, userDelivery) {
        const url = apiTools.prependDomain(`${API_ROOT}StoreUserDelivery`);
        const request = apiTools.makeJsonPostRequest(url, {
            assessmentRunId,
            userDelivery,
        });

        return apiTools.executeRequest(request);
    },

    requestAdaptiveQuestionSet(assessmentRunId, itemRevisionIds) {
        const url = apiTools.prependDomain(`${API_ROOT}TriggerCAT`);
        const request = apiTools.makeFormDataPostRequest(url, {
            assessmentRunId,
            itemRevisionIds,
        });

        return apiTools.executeRequest(request);
    },

    postCatQuestionSetId(assessmentRunId, catRevisionId) {
        const url = apiTools.prependDomain(`${API_ROOT}StoreCatQuestionSetLatestRevision`);
        const request = apiTools.makeFormDataPostRequest(url, {
            assessmentRunId,
            catRevisionId,
        });

        return apiTools.executeRequest(request);
    },

    // -----------------
    // TODO: TEII-2977 - move LiveSession related api call tho the corresponding module

    startLiveSession(assessmentRunId) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.START_LIVE_SESSION}`);
        const request = apiTools.makeFormDataPostRequest(path, {
            assessmentRunId,
        });

        return apiTools.executeRequest(request);
    },

    sendLiveSessionAnswer(answer, receiverContributorId) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.SEND_LIVE_SESSION_ANSWER}`);
        const request = apiTools.makeFormDataPostRequest(path, {
            receiverContributorId,
            answer: JSON.stringify(answer),
        });

        return apiTools.executeRequest(request);
    },

    sendLiveSessionRecoveryAnswer(answer, receiverContributorId) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.SEND_LIVE_SESSION_RECOVERY_ANSWER}`);
        const request = apiTools.makeFormDataPostRequest(path, {
            receiverContributorId,
            answer: JSON.stringify(answer),
        });

        return apiTools.executeRequest(request);
    },

    evictLiveSessionProctor(evictedContributorId) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.EVICT_PROCTOR_FROM_LIVE_SESSION}`);
        const request = apiTools.makeFormDataPostRequest(path, {
            evictedContributorId,
        });

        return apiTools.executeRequest(request);
    },

    sendLiveSessionIceCandidates(iceCandidates, receiverContributorId) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.SEND_LIVE_SESSION_ICE_CANDIDATES}`);
        const request = apiTools.makeFormDataPostRequest(path, {
            receiverContributorId,
            iceCandidates: JSON.stringify(iceCandidates),
        });

        return apiTools.executeRequest(request);
    },

    sendLiveSessionBackOnlineNotice(assessmentRunId) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.SEND_LIVE_SESSION_BACK_ONLINE_NOTICE}`);
        const request = apiTools.makeFormDataPostRequest(path, {
            assessmentRunId,
        });

        return apiTools.executeRequest(request);
    },

    sendLiveSessionCandidateOnlineConfirmation(messagerId) {
        const path = apiTools.prependDomain(`${API_ROOT}${methods.SEND_LIVE_SESSION_CANDIDATE_ONLINE_CONFIRMATION}`);
        const request = apiTools.makeFormDataPostRequest(path, {
            receiverContributorId: messagerId,
        });

        return apiTools.executeRequest(request);
    },

    // IEP uses this request as a signal that the Test is fully delivered
    sendCompletedFakeRequest() {
        return apiTools.makeFakeRequest(safeBrowserLinks.COMPLETED);
    },

    // IEP uses this request as a signal to clear clipboard
    sendIEPClipboardClearingRequest() {
        return apiTools.makeFakeRequest(safeBrowserLinks.CLEAR_CLIPBOARD);
    },

    // IEP uses this request as a signal to disable exit button
    sendDisableExitUrl() {
        return apiTools.makeFakeRequest(safeBrowserLinks.DISABLE_EXIT_URL);
    },

    // IEP uses this request as a signal to enable exit button
    sendEnableExitUrl() {
        return apiTools.makeFakeRequest(safeBrowserLinks.ENABLE_EXIT_URL);
    },

    postProctoringSessionEstablishedNotice(assessmentRunId) {
        const url = apiTools.prependDomain(`${API_ROOT}${methods.SEND_PROCTORING_SESSION_ESTABLISHED_NOTICE}`);
        const request = apiTools.makeFormDataPostRequest(url, {
            assessmentRunId,
        });

        return apiTools.executeRequest(request);
    },

    // IEP uses this request as a signal that the hardware prechecks are completed and sends selected microphone
    sendPrecheckCompleteSignal(deviceName) {
        let signalUrl = safeBrowserLinks.MIC_WEBCAM_HARDWARE_PRECHECK_COMPLETE;
        if (deviceName) {
            const encodedDeviceName = encodeURIComponent(deviceName);
            signalUrl = `${signalUrl}?deviceName=${encodedDeviceName}`;
        }
        return apiTools.makeFakeRequest(signalUrl);
    },

    /**
     * Iceworm uses this request as a signal that the user has tabbed through the last page.
     * After receiving this signal, Iceworm should move focus to its toolbar.
     * @param {string} [source] - Optional. Indicates the tab direction ('forward' or 'backward').
     * If omitted or an unexpected value, no query parameter is added.
     *                          This helps Iceworm understand the navigation context.
     */
    sendLastPageTabNavigationSignal(source) {
        let signalUrl = safeBrowserLinks.LAST_PAGE_TAB_NAVIGATION_COMPLETE;
        if (source === 'forward' || source === 'backward') {
            signalUrl += `?source=${encodeURIComponent(source)}`;
        }
        return apiTools.makeFakeRequest(signalUrl);
    },

    /**
     * Sends a lightweight browser signal to trigger opening Microsoft Excel in IIB.
     * The questionId is appended as a query parameter so the launcher can fetch the
     * WOPI URL directly from platform-ia (no JWT in the signal URL).
     *
     * @param {number} questionId - The question ID for the O365 spreadsheet.
     * @returns {Promise<*>} A promise returned by `apiTools.makeFakeRequest` for the generated signal URL.
     */
    sendOpenExcelWindowSignal(questionId) {
        const signalUrl = `${safeBrowserLinks.IIB_OPEN_MSFT_EXCEL_URL}?questionId=${questionId}`;
        return apiTools.makeFakeRequest(signalUrl);
    },
};

export default apiService;
